/**
 * WE-115: identity/session context shared by every analytics destination.
 *
 * This module is intentionally NOT a wrapper around `events.emit()` — the
 * event bus itself stays a plain typed EventEmitter (src/events/core/index.ts).
 * Instead, destinations (currently only src/events/gtag/index.ts) call
 * `getEventContext()` when building their payload, so `anonymous_id`,
 * `user_id`, `session_id`, `event_id` and `service` are attached consistently
 * without every call site in the app having to know about them.
 *
 * Fixes the pre-WE-115 bug where identity lived in a gtag-adapter-local
 * module variable that was never cleared on logout (PII of the previous
 * user could leak into the next user's events within the same tab).
 */
import { isSSR } from '@/utils/helper';
import { getCookie } from '@/utils/cookies';
import { ACTIVE_SERVICE_COOKIE } from '@Constants/navigation.contants';
import { pathToServiceName } from '@Constants/services.meta';
import { getFirstTouchAttribution } from '@/auth/attribution';
import { events } from './index';

const ANONYMOUS_ID_KEY = 'wizium_anonymous_id';
const SESSION_ID_KEY = 'wizium_session_id';
const SESSION_STARTED_AT_KEY = 'wizium_session_started_at';
const SESSION_LAST_ACTIVE_KEY = 'wizium_session_last_active';
const SESSION_EVENTS_COUNT_KEY = 'wizium_session_events_count';
const SESSION_PAGES_VIEWED_KEY = 'wizium_session_pages_viewed';

/** Industry default (GA4/Mixpanel) — decided with product, see WE-115 spec §9. */
export const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

let userId: string | null = null;

function generateId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    // Fallback for environments without crypto.randomUUID (older WebViews).
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

// WE-115 fix: ACTIVE_SERVICE_COOKIE is written as a full route (e.g. '/hub',
// '/scout' — see useActiveService.tsx/proxy.ts), never the bare 'hub'/'scout'
// this used to compare against, so this always returned null. Every event's
// `service` context field has been null since Wave 0 until this fix.
export function getService(): 'hub' | 'scout' | null {
    if (isSSR) return null;
    const value = getCookie(ACTIVE_SERVICE_COOKIE);
    return value ? pathToServiceName(value) : null;
}

export function getAnonymousId(): string {
    if (isSSR) return '';
    let id = localStorage.getItem(ANONYMOUS_ID_KEY);
    if (!id) {
        id = generateId();
        localStorage.setItem(ANONYMOUS_ID_KEY, id);
    }
    return id;
}

export function getUserId(): string | null {
    return userId;
}

/** Call right after a successful login/sign_up, before other events of the session need it. */
export function setUserId(id: string): void {
    userId = id;
}

/**
 * Call on logout. Clears the resolved user_id AND rotates anonymous_id, so a
 * different person logging in on the same device/tab never inherits the
 * previous user's identity in outgoing events.
 */
export function resetIdentity(): void {
    userId = null;
    if (!isSSR) {
        localStorage.removeItem(ANONYMOUS_ID_KEY);
    }
}

/**
 * Ends whatever session is currently recorded in localStorage.
 *
 * The `session_end` emit is bracketed by the `endingSession` flag because
 * destination adapters resolve their context while handling it (gtag's
 * handler calls `getEventContext()` -> `touchSession()`). Without the flag
 * that re-entry either recursed without bound (same stale session found
 * again) or — the earlier clear-keys-first fix — started a phantom
 * replacement session that was persisted on every tab close/logout and
 * emitted a spurious `session_start`. With the flag, the re-entrant
 * touchSession just reports the ending session's id, so the session_end
 * push carries the correct context, and the keys are only cleared after
 * the emit completes.
 */
let endingSession = false;

function endSession(): void {
    if (isSSR || endingSession) return;
    const sessionId = localStorage.getItem(SESSION_ID_KEY);
    const lastActive = Number(localStorage.getItem(SESSION_LAST_ACTIVE_KEY) ?? 0);
    if (!sessionId || !lastActive) return;

    const startedAt = Number(localStorage.getItem(SESSION_STARTED_AT_KEY) ?? lastActive);
    const pagesViewed = Number(localStorage.getItem(SESSION_PAGES_VIEWED_KEY) ?? 0);
    const eventsCount = Number(localStorage.getItem(SESSION_EVENTS_COUNT_KEY) ?? 0);

    endingSession = true;
    try {
        events.emit('session_end', {
            session_id: sessionId,
            duration_sec: Math.max(0, Math.round((lastActive - startedAt) / 1000)),
            pages_viewed: pagesViewed,
            events_count: eventsCount,
        });
    } finally {
        endingSession = false;
        localStorage.removeItem(SESSION_ID_KEY);
        localStorage.removeItem(SESSION_STARTED_AT_KEY);
        localStorage.removeItem(SESSION_LAST_ACTIVE_KEY);
        localStorage.removeItem(SESSION_EVENTS_COUNT_KEY);
        localStorage.removeItem(SESSION_PAGES_VIEWED_KEY);
    }
}

function startSession(entryPoint: string): string {
    const sessionId = generateId();
    const now = Date.now();
    localStorage.setItem(SESSION_ID_KEY, sessionId);
    localStorage.setItem(SESSION_STARTED_AT_KEY, String(now));
    localStorage.setItem(SESSION_LAST_ACTIVE_KEY, String(now));
    localStorage.setItem(SESSION_EVENTS_COUNT_KEY, '0');
    localStorage.setItem(SESSION_PAGES_VIEWED_KEY, '0');

    // WE-115: prefer the persisted first-touch attribution (src/auth/attribution.ts)
    // over parsing the CURRENT page's query string — a session can start on any
    // page (e.g. after a 30-min timeout deep in the app), long after the
    // landing-page URL that actually carried the utm/click-id params is gone.
    const firstTouch = getFirstTouchAttribution();
    const params = new URLSearchParams(window.location.search);
    events.emit('session_start', {
        session_id: sessionId,
        entry_point: entryPoint,
        referrer: document.referrer || '',
        utm_source: firstTouch?.utm_source ?? params.get('utm_source') ?? undefined,
        utm_medium: firstTouch?.utm_medium ?? params.get('utm_medium') ?? undefined,
        utm_campaign: firstTouch?.utm_campaign ?? params.get('utm_campaign') ?? undefined,
        gclid: firstTouch?.gclid ?? params.get('gclid') ?? undefined,
        fbclid: firstTouch?.fbclid ?? params.get('fbclid') ?? undefined,
    });

    return sessionId;
}

/** Starts or renews the session as needed and returns its id — without counting an event. */
function ensureSession(entryPoint: string = ''): string {
    const now = Date.now();
    const existingId = localStorage.getItem(SESSION_ID_KEY);
    const lastActive = Number(localStorage.getItem(SESSION_LAST_ACTIVE_KEY) ?? 0);

    if (!existingId || now - lastActive > SESSION_TIMEOUT_MS) {
        if (existingId) {
            endSession();
        }
        return startSession(entryPoint || window.location.pathname);
    }
    return existingId;
}

/**
 * Ensures a session exists (starting/renewing it as needed) and marks
 * activity. Call this on every tracked event and on every page_view.
 */
export function touchSession(entryPoint: string = ''): string {
    if (isSSR) return '';
    // Re-entrancy guard: destination adapters resolve their context while
    // handling the session_end emit itself — report the ending session's id
    // without renewing activity or starting a replacement session.
    if (endingSession) return localStorage.getItem(SESSION_ID_KEY) ?? '';

    const sessionId = ensureSession(entryPoint);

    localStorage.setItem(SESSION_LAST_ACTIVE_KEY, String(Date.now()));
    localStorage.setItem(
        SESSION_EVENTS_COUNT_KEY,
        String(Number(localStorage.getItem(SESSION_EVENTS_COUNT_KEY) ?? 0) + 1)
    );
    return sessionId;
}

export function trackPageViewedInSession(): void {
    if (isSSR || endingSession) return;
    // WE-115 fix: make sure the session exists BEFORE incrementing.
    // Previously the increment landed first and the page_view emit that
    // follows it started the session, whose counter reset to '0' wiped the
    // increment — every session under-reported pages_viewed by one, and a
    // single-page bounce reported zero pages. ensureSession (not
    // touchSession) so the page itself isn't double-counted in events_count.
    ensureSession();
    localStorage.setItem(
        SESSION_PAGES_VIEWED_KEY,
        String(Number(localStorage.getItem(SESSION_PAGES_VIEWED_KEY) ?? 0) + 1)
    );
}

/** Registers best-effort session_end on tab close. */
export function registerSessionEndOnUnload(): () => void {
    if (isSSR) return () => {};

    window.addEventListener('beforeunload', endSession);
    return () => window.removeEventListener('beforeunload', endSession);
}

export function endSessionOnLogout(): void {
    endSession();
}

/** Merged into every outgoing destination payload (see gtag adapter). */
export function getEventContext() {
    return {
        event_id: generateId(),
        anonymous_id: getAnonymousId(),
        user_id: getUserId(),
        session_id: touchSession(),
        service: getService(),
        timestamp: new Date().toISOString(),
    };
}
